import { useState, useRef, useCallback, useEffect } from "preact/hooks";
import { useTranslations } from "../../hooks/useTranslations";

type FilterOption = {
  id: string;
  label: string;
};

type FilterDropdownProps = {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  className?: string;
};

export function FilterDropdown({
  label,
  options,
  selected,
  onChange,
  className = "",
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations();

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
      return !prev;
    });
  }, []);

  const handleOptionClick = useCallback(
    (id: string) => {
      if (selected.includes(id)) {
        onChange(selected.filter((s) => s !== id));
      } else {
        onChange([...selected, id]);
      }
    },
    [selected, onChange]
  );

  const handleClear = useCallback(() => {
    onChange([]);
    setSearch("");
  }, [onChange]);

  const handleBackdropClick = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("click", handleBackdropClick);
    return () => document.removeEventListener("click", handleBackdropClick);
  }, [isOpen, handleBackdropClick]);

  const selectedCount = selected.length;
  const showBadge = selectedCount > 0;

  return (
    <div className={`festapp-filter-dropdown ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className={`festapp-filter-dropdown-trigger ${isOpen ? "festapp-filter-dropdown-trigger-active" : ""}`}
        onClick={handleToggle}
      >
        <span>{label}</span>
        {showBadge && (
          <span className="festapp-filter-dropdown-badge">{selectedCount}</span>
        )}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`festapp-filter-dropdown-chevron ${isOpen ? "festapp-filter-dropdown-chevron-open" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="festapp-filter-dropdown-menu">
          <div className="festapp-filter-dropdown-search">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
              placeholder={t.search}
              className="festapp-filter-dropdown-search-input"
            />
          </div>

          {selectedCount > 0 && (
            <div className="festapp-filter-dropdown-actions">
              <button
                type="button"
                className="festapp-filter-dropdown-action"
                onClick={handleClear}
              >
                {t.clear}
              </button>
            </div>
          )}

          <div className="festapp-filter-dropdown-options">
            {filteredOptions.length === 0 ? (
              <div className="festapp-filter-dropdown-empty">{t.noResults}</div>
            ) : (
              filteredOptions.map((option) => (
                <label
                  key={option.id}
                  className="festapp-filter-dropdown-option"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option.id)}
                    onChange={() => handleOptionClick(option.id)}
                    className="festapp-filter-dropdown-checkbox"
                  />
                  <span className="festapp-filter-dropdown-option-label">
                    {option.label}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
